import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { isMobileOnly } from "react-device-detect";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Popover from "@mui/material/Popover";
import List from "@mui/material/List";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import isEmpty from "lodash/isEmpty";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import orderBy from "lodash/orderBy";
import { notify, notifySimple } from "../../themes/components/Notifications";
import { getLocalTime } from "../../utils/Time";
import { usePagination } from "../../hooks/usePagination";
import useSharedStyles from "../../styles/Shared";
import useButtonStyles from "../../styles/Buttons";
import usePopoverStyles from "../../styles/Popovers";
import { deleteSupplier } from "../../api/Supplier";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import SearchIcon from "@mui/icons-material/Search";
import { InputAdornment, ListItemButton, Paper, Divider } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { COLORS } from "../../themes/Base";
import ChevronDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ChevronUpIcon from "@mui/icons-material/KeyboardArrowUp";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "../../components/Icons/DeleteIcon";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import DeleteConfirmationDialog from "../../components/DeleteConfirmationDialog";
import StickyPagination from "../../components/StickyPagination";

type Props = {
  suppliers: Supplier[];
  refetch: () => void;
};

const SORT_OPTIONS = [
  { value: "inserted_at:desc", label: "Created Date (Newest first)" },
  { value: "inserted_at:asc", label: "Created Date (Oldest first)" },
  { value: "name:asc", label: "Supplier Name (A–Z)" },
  { value: "name:desc", label: "Supplier Name (Z–A)" },
];

const SuppliersList: React.FC<Props> = ({ suppliers, refetch }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [itemEdit, setItemEdit] = useState<Supplier | null>(null);
  const [itemDelete, setItemDelete] = useState<string | null>(null);
  const [searchPhrase, setSearchPhrase] = useState("");
  const [sortBy, setSortBy] = useState("inserted_at:desc");
  const [perPage, setPerPage] = useState(50);

  const { classes } = useSharedStyles();
  const { classes: buttonClasses, cx } = useButtonStyles();

  const onDeleteSubmit = async (values: SupplierDeleteVars) => {
    try {
      const response = await deleteSupplier(values.id);
      const { messages, result } = response.supplierDelete;

      if (!isEmpty(messages)) {
        notify(messages);
      } else if (result) {
        notifySimple(["Supplier deleted"], "success");
        refetch();
      }
    } catch (err) {
      notifySimple(["Error deleting Supplier"], "error");
    }
  };

  const updateSearchPhrase = (newSearchPhrase: string) => {
    onPageChange(1);
    setSearchPhrase(newSearchPhrase);
  };

  const [sortField, sortOrderValue] = sortBy.split(":");
  const filteredSuppliers = orderBy(
    suppliers.filter(
      (supplier) => supplier.name.toLowerCase().indexOf(searchPhrase.toLowerCase()) !== -1
    ),
    [sortField === "name" ? (el) => el.name.toLowerCase().trim() : sortField],
    [sortOrderValue as "asc" | "desc"]
  );

  const { currentPage, offset, onPageChange } = usePagination(
    filteredSuppliers.length,
    perPage,
    true
  );

  return (
    <div>
      <Box className={classes.stickyHeader}>
        <Box
          display="flex"
          width={isMobileOnly ? "100%" : "auto"}
          alignItems="center"
          flexDirection={isMobileOnly ? "column" : "row"}
          justifyContent="space-between"
          marginBottom="24px"
        >
          <Typography variant="h1" sx={{ paddingBottom: 1, marginRight: 4 }}>
            Suppliers
          </Typography>
          <Button
            className={cx(buttonClasses.blue, buttonClasses.iconButton)}
            component={NavLink}
            to="/supplier/create"
            sx={{ "& .MuiSvgIcon-root": { mr: 0 } }}
          >
            <AddIcon /> Add Supplier
          </Button>
        </Box>
        <Box
          display="flex"
          width="100%"
          alignItems="center"
          gap={isMobileOnly ? 0 : 2}
          flexDirection={isMobileOnly ? "column" : "row"}
        >
          <Autocomplete
            sx={{ flex: 1, minWidth: 0 }}
            freeSolo
            autoSelect={false}
            options={[
              ...new Set(
                orderBy(suppliers, [(el) => el.name.toLowerCase().trim()], ["asc"])
                  .map((supplier) => supplier.name.trim())
              ),
            ]}
            value={searchPhrase}
            onChange={(_, v) => {
              updateSearchPhrase(v || "");
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search for Suppliers"
                autoFocus
                onChange={(e) => updateSearchPhrase(e.target.value)}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" sx={{ color: "#67676C", width: "20px" }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchPhrase ? (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="Clear search"
                        edge="end"
                        size="small"
                        onClick={() => updateSearchPhrase("")}
                      >
                        <ClearIcon sx={{ color: "#67676C", width: "20px" }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            )}
            PaperComponent={(props) => (
              <Paper {...props} className={classes.innerBox} sx={{ fontSize: 14 }} />
            )}
          />
          <FormControl sx={{ width: isMobileOnly ? "100%" : 281 }}>
            <Select
              fullWidth
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value);
              }}
              renderValue={(selected) => {
                const selectedOption = SORT_OPTIONS.find((opt) => opt.value === selected);
                return selectedOption ? (
                  <span style={{ color: "#67676C" }}>{`Sort by: ${selectedOption.label}`}</span>
                ) : (
                  ""
                );
              }}
              IconComponent={(props) =>
                props.className?.includes("MuiSelect-iconOpen") ? (
                  <ChevronUpIcon sx={{ color: COLORS.iconColor }} />
                ) : (
                  <ChevronDownIcon sx={{ color: COLORS.iconColor }} />
                )
              }
              MenuProps={{
                PaperProps: {
                  className: classes.innerBox,
                },
              }}
            >
              {SORT_OPTIONS.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  sx={{
                    fontSize: 14,
                    margin: "8px",
                    "&.Mui-selected, &:hover, &.Mui-selected:hover": {
                      backgroundColor: "#EAEAED",
                      color: "inherit",
                      borderRadius: "8px",
                    },
                  }}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {isEmpty(suppliers) ? (
        <Alert severity="info">
          <AlertTitle>No available Suppliers</AlertTitle>
          Use the button on the right to add a new Supplier
        </Alert>
      ) : (
        <>
          <Box sx={{ height: "calc(100vh - 250px)", mt: 25, pb: 8, overflowY: "scroll" }}>
            {filteredSuppliers.slice(offset, offset + perPage).map((supplier) => (
              <Box
                key={supplier?.id}
                sx={{
                  borderRadius: "12px",
                  border: "1px solid #E4E8D3",
                  background: "#fff",
                  boxShadow: "0 1px 2px 0 rgba(16, 24, 40, 0.04)",
                  mb: 2,
                  mx: 4,
                  p: "16px",
                  position: "relative",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: "8px", color: COLORS.textMain }}>
                  <NavLink
                    className={cx(classes.itemName, "link")}
                    to={`/supplier/${supplier.id}/view`}
                  >
                    {supplier.name}
                  </NavLink>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, color: COLORS.textSecondary }}>
                  <Typography sx={{ fontSize: 14 }}>Created: {getLocalTime(supplier.inserted_at).format("DD/MM/YYYY HH:mm:ss")}</Typography>
                  {supplier.updated_at !== supplier.inserted_at && (
                    <Typography sx={{ fontSize: 14 }}>
                      Updated: {getLocalTime(supplier.updated_at).format("DD/MM/YYYY HH:mm:ss")}
                    </Typography>
                  )}
                </Box>
                <IconButton
                  sx={{ position: "absolute", right: "16px", top: "21px" }}
                  onClick={(e) => {
                    setAnchorEl(e.currentTarget);
                    setItemEdit(supplier);
                  }}
                  className={classes.settingsButton}
                >
                  <MoreVertIcon sx={{ color: "#4F4F52" }} />
                </IconButton>
              </Box>
            ))}
            {filteredSuppliers.length > 0 && (
              <StickyPagination
                count={filteredSuppliers.length}
                page={currentPage}
                rowsPerPage={perPage}
                onPageChange={onPageChange}
                onRowsPerPageChange={(n) => {
                  setPerPage(n);
                  onPageChange(1);
                }}
              />
            )}
          </Box>
        </>
      )}

      <Popover
        anchorEl={anchorEl}
        open={Boolean(anchorEl && itemEdit)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        disableScrollLock
        PaperProps={{
          className: cx(classes.innerBox),
        }}
      >
        <List className={classes.dropdownMenu}>
          <ListItemButton
            onClick={() => {
              setAnchorEl(null);
              navigate(`/supplier/${itemEdit?.id}/view`);
            }}
            className={classes.dropdownMenuItem}
          >
            <VisibilityIcon /> View Supplier
          </ListItemButton>
          <ListItemButton
            onClick={() => {
              setAnchorEl(null);
              navigate(`/supplier/${itemEdit?.id}/edit`);
            }}
            className={classes.dropdownMenuItem}
          >
            <EditIcon /> Edit Supplier
          </ListItemButton>
          <ListItemButton
            onClick={() => {
              setAnchorEl(null);
              navigate("/supplier/create", {
                state: { initialValues: itemEdit },
              });
            }}
            className={classes.dropdownMenuItem}
          >
            <FileCopyIcon /> Duplicate & Add Supplier
          </ListItemButton>
          <Divider />
          <ListItemButton
            onClick={() => {
              setAnchorEl(null);
              setItemDelete(itemEdit?.id || null);
            }}
            className={cx(classes.dropdownMenuItem, "red")}
          >
            <DeleteIcon /> Delete Supplier
          </ListItemButton>
        </List>
      </Popover>

      <DeleteConfirmationDialog
        open={Boolean(itemDelete)}
        onCancel={() => setItemDelete(null)}
        onConfirm={() => {
          if (itemDelete) {
            void onDeleteSubmit({ id: itemDelete });
            setItemDelete(null);
          }
        }}
        title="Are you sure you want to delete this supplier?"
        description="Deleting this supplier will permanently remove all associated data. This action cannot be undone."
      />
    </div>
  );
};

export default SuppliersList;
